import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import { GmailScope, UserIntegration } from "schemas/dashboard";
import { Map } from "schemas/functions";

import GoogleConnect from "features/Settings/GoogleConnect";
import { isCommonDomain } from "utils/emails";

import styles from "./styles";

interface IProps {
  openIntegrationDialog: boolean;
  setOpenIntegrationDialog: (newOpen: boolean) => void;
  emailHealth: boolean;
  userIntegrations: UserIntegration[];
  userIntegration: UserIntegration | null;
  scopes: Map | null;
  onSuccessAction?: () => Promise<void>;
  integrationDialogText?: string;
  subtitle?: string;
}

const PERMISSIONS = {
  [GmailScope.SEND]: {
    title: "Send emails on your behalf",
    subtitle: "Send personalized pitches directly to brands",
  },
  [GmailScope.READ]: {
    title: "View email messages",
    subtitle: "Track responses and schedule smart follow-ups",
  },
};

export default function ConnectGmailDialog({
  openIntegrationDialog,
  setOpenIntegrationDialog,
  emailHealth,
  userIntegrations,
  userIntegration,
  scopes,
  onSuccessAction,
  integrationDialogText,
  subtitle = "Required to send brand emails",
}: IProps) {
  const close = () => setOpenIntegrationDialog(false);

  const renderPermissions = (scopesToShow: GmailScope[]) => (
    <Box sx={styles.permissionList}>
      {scopesToShow.map((scope) => (
        <Box key={scope} sx={styles.permissionItem}>
          <Box sx={styles.permissionCheckCircle}>
            <Box
              component="i"
              className="fa-solid fa-circle-check"
              sx={styles.permissionCheckIcon}
            />
          </Box>
          <Box>
            <Typography sx={styles.permissionTitle}>
              {PERMISSIONS[scope].title}
            </Typography>
            <Typography sx={styles.permissionSubtitle}>
              {PERMISSIONS[scope].subtitle}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );

  const renderPrivacyNote = () => (
    <Box sx={styles.privacyBox}>
      <Box
        component="i"
        className="fa-solid fa-shield-halved"
        sx={styles.shieldIcon}
      />
      <Typography sx={styles.privacyText}>
        <strong>Your privacy is protected.</strong> Bento will only access
        emails useful to helping you secure brand partnerships.
      </Typography>
    </Box>
  );

  const renderConnectFlow = (
    warningTitle: string,
    warningText: string,
    scopesToShow: GmailScope[],
  ) => (
    <>
      <Box sx={styles.warningBox}>
        <Typography sx={styles.warningTitle}>{warningTitle}</Typography>
        <Typography sx={styles.warningText}>{warningText}</Typography>
      </Box>

      <Typography sx={styles.sectionLabel}>
        Sign in with Google and grant these permissions:
      </Typography>
      {renderPermissions(scopesToShow)}

      {renderPrivacyNote()}

      <GoogleConnect onSuccessAction={onSuccessAction} fullWidth />
      <Button onClick={close} sx={styles.laterButton}>
        I'll do this later
      </Button>
    </>
  );

  const renderErrorFlow = () => (
    <>
      <Box sx={styles.warningBox}>
        {isCommonDomain(userIntegration?.email ?? "") ? (
          <Typography sx={styles.warningText}>
            You are currently attempting to connect Bento with a{" "}
            <strong>non-Gmail account.</strong> You need to connect an inbox
            that runs on Gmail to send emails with Bento. Please create an
            @gmail.com account and try again — see{" "}
            <a
              target="_blank"
              rel="noreferrer"
              href="https://support.google.com/accounts/answer/27441?hl=en&co=GENIE.Platform%3DDesktop&oco=0"
              style={styles.alertLink}
            >
              this guide
            </a>
            .
          </Typography>
        ) : (
          <Typography sx={styles.warningText}>
            This Google account does not have Gmail enabled. If you use Google
            Workspace, your domain must be{" "}
            <a
              target="_blank"
              rel="noreferrer"
              href="https://support.google.com/a/answer/60216?hl=en"
              style={styles.alertLink}
            >
              verified
            </a>{" "}
            and{" "}
            <a
              target="_blank"
              rel="noreferrer"
              href="https://support.google.com/a/answer/172171?hl=en"
              style={styles.alertLink}
            >
              Gmail must be activated
            </a>
            . Otherwise, use an @gmail.com account and try again.
          </Typography>
        )}
      </Box>

      <Typography sx={styles.descriptionText}>
        Questions? Reach out to{" "}
        <a href="mailto:hello@onbento.com" style={styles.alertLink}>
          hello@onbento.com
        </a>{" "}
        for assistance.
      </Typography>

      <GoogleConnect onSuccessAction={onSuccessAction} fullWidth />
      <Button onClick={close} sx={styles.laterButton}>
        I'll do this later
      </Button>
    </>
  );

  const renderContent = () => {
    if (emailHealth) {
      return (
        <Box sx={styles.successBox}>
          <Box
            component="i"
            className="fa-solid fa-circle-check"
            sx={styles.successIcon}
          />
          <Typography sx={styles.permissionTitle}>
            Success! You are connected.
          </Typography>
          <Button
            variant="contained"
            disableElevation
            onClick={close}
            sx={styles.doneButton}
          >
            Done
          </Button>
        </Box>
      );
    }

    if (integrationDialogText) {
      return (
        <>
          <Typography sx={styles.descriptionText}>
            {integrationDialogText}
          </Typography>
          {renderPrivacyNote()}
          <GoogleConnect onSuccessAction={onSuccessAction} fullWidth />
          <Button onClick={close} sx={styles.laterButton}>
            I'll do this later
          </Button>
        </>
      );
    }

    if (userIntegrations?.length === 0) {
      return renderConnectFlow(
        "Permissions needed",
        "In order to send emails, you must grant Bento access.",
        [GmailScope.SEND, GmailScope.READ],
      );
    }

    if (
      userIntegrations &&
      userIntegrations.length >= 1 &&
      scopes !== null &&
      (!scopes[GmailScope.SEND] || !scopes[GmailScope.READ])
    ) {
      const missingScopes = [GmailScope.SEND, GmailScope.READ].filter(
        (scope) => !scopes[scope],
      );
      return renderConnectFlow(
        "Additional permissions needed",
        "In order to send emails, you must grant Bento additional access.",
        missingScopes,
      );
    }

    if (userIntegration?.integrationError && userIntegration?.email) {
      return renderErrorFlow();
    }

    return renderConnectFlow(
      "Reconnect your Gmail",
      "In order to keep sending emails, you must reconnect Bento.",
      [GmailScope.SEND, GmailScope.READ],
    );
  };

  return (
    <Dialog
      open={openIntegrationDialog}
      onClose={close}
      maxWidth="sm"
      PaperProps={{ sx: styles.dialogPaper }}
    >
      <Box sx={styles.header}>
        <IconButton onClick={close} sx={styles.closeButton} size="small">
          <Box
            component="i"
            className="fa-solid fa-times"
            sx={{ fontSize: 16 }}
          />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={styles.mailIconCircle}>
            <Box
              component="i"
              className="fa-solid fa-envelope"
              sx={styles.mailIcon}
            />
          </Box>
          <Box>
            <Typography
              fontWeight={700}
              fontSize="1.25rem"
              sx={{ color: "white" }}
            >
              Connect Your Gmail
            </Typography>
            <Typography sx={styles.headerSubtitle}>{subtitle}</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={styles.content}>{renderContent()}</Box>
    </Dialog>
  );
}
